import React, { useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import NRRiseAiIcon from '../../../NRIcons/NRRiseAiIcon';
import ComponentTable from './ComponentTable';
import NRRiseAIVulnerabilitiesTable from '../../NRVulnerabilitiesTable/NRRiseAIVulnerabilitiesTable';
import { toVexStatusFromRest } from '../utils/messageUtils';

export default function MessageItem({ message, toolCalls, showToolCalls }) {
  const theme = useTheme();
  const vulnerabilitiesTableRef = useRef(null);

  const nrvulnerabilities = useMemo(() => {
    const vulns = message?.vulnerabilities;
    if (!Array.isArray(vulns) || vulns.length === 0) return [];
    console.log(vulns);
    return vulns.map(v => ({
      // Map AI response fields -> NRVulnerabilitiesTable expected row shape
      cve: v?.vulnerability_name || v?.cve || '',
      severity: v?.severity,
      name: v?.component_name,
      vendor: v?.component_vendor,
      version: v?.component_version,
      epssScore: v?.epss_score,
      epssPercentile: v?.epss_percentile,
      isReachable: v?.reachable,
      maturity: v?.maturity,
      attackVector: v?.attack_vector,
      attackComplexity: v?.attack_complexity,
      inKnownExploitedVulnerabilities: v?.in_known_exploited_vulnerabilities,
      filePath: v?.file_path,
      correlations: typeof v?.exploits === 'number' ? v.exploits : 0,
      currentRemediation: v?.remediation_status ? { status: toVexStatusFromRest(v.remediation_status) } : undefined
    }));
  }, [message?.vulnerabilities]);

  return (
    <React.Fragment>
      {/* Show tool calls in real-time as they arrive, only for the current assistant message */}
      {showToolCalls && (
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
          {Object.entries(toolCalls).map(([toolCallId, toolCall]) => {
            if (!toolCall || !toolCall.name) return null;
            return (
              <Chip
                key={toolCallId}
                label={toolCall.name}
                size="small"
                sx={{
                  backgroundColor: theme.palette.riseAiPurple,
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: theme.typography.caption.fontSize
                }}
              />
            );
          })}
        </Box>
      )}
      {/* Only render message box if there's content, components, vulnerabilities, or error */}
      {(message.content ||
        (message.components && message.components.length > 0) ||
        (message.vulnerabilities && message.vulnerabilities.length > 0) ||
        message.error) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            mb: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: '100%',
              padding: theme.spacing(1.5, 2),
              backgroundColor: message.role === 'user' ? theme.palette.riseAiPurple : theme.palette.background.paper,
              color: message.role === 'user' ? theme.palette.common.white : theme.palette.text.primary,
              borderRadius: theme.shape.borderRadius * 2,
              border: message.role === 'assistant' ? `1px solid ${theme.palette.divider}` : 'none'
            }}
          >
            {message.role === 'assistant' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <NRRiseAiIcon height="16" width="16" color={theme.palette.riseAiPurple} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                  RiseAI
                </Typography>
              </Box>
            )}
            {message.content && (
              <>
                {message.role === 'assistant' && !message.error ? (
                  <Box
                    sx={{
                      '& p': {
                        margin: 0,
                        marginBottom: 1,
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& ul, & ol': {
                        margin: 0,
                        marginBottom: 1,
                        paddingLeft: 2,
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        margin: 0,
                        marginTop: 1,
                        marginBottom: 0.5,
                        fontWeight: 600,
                        '&:first-of-type': {
                          marginTop: 0
                        }
                      },
                      '& code': {
                        backgroundColor: theme.palette.action.hover,
                        padding: theme.spacing(0.25, 0.5),
                        borderRadius: theme.shape.borderRadius,
                        fontFamily: theme.typography.fontFamilyMonospace || 'monospace',
                        fontSize: theme.typography.body2.fontSize
                      },
                      '& pre': {
                        backgroundColor: theme.palette.action.hover,
                        padding: theme.spacing(1),
                        borderRadius: theme.shape.borderRadius,
                        overflow: 'auto',
                        margin: 0,
                        marginBottom: 1,
                        '&:last-child': {
                          marginBottom: 0
                        },
                        '& code': {
                          backgroundColor: 'transparent',
                          padding: 0
                        }
                      },
                      '& blockquote': {
                        borderLeft: `3px solid ${theme.palette.divider}`,
                        paddingLeft: theme.spacing(1),
                        margin: 0,
                        marginBottom: 1,
                        fontStyle: 'italic',
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& table': {
                        borderCollapse: 'collapse',
                        width: '100%',
                        marginBottom: 1,
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& th, & td': {
                        border: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(0.5, 1),
                        textAlign: 'left'
                      },
                      '& th': {
                        backgroundColor: theme.palette.action.hover,
                        fontWeight: 600
                      },
                      '& a': {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      },
                      '& hr': {
                        border: 'none',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        margin: theme.spacing(1, 0)
                      }
                    }}
                  >
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: message.error ? theme.typography.fontFamilyMonospace || 'monospace' : 'inherit',
                      fontSize: message.error ? theme.typography.caption.fontSize : 'inherit'
                    }}
                  >
                    {message.content}
                  </Typography>
                )}
              </>
            )}
            {message.components && message.components.length > 0 && <ComponentTable components={message.components} />}
            {message.vulnerabilities && message.vulnerabilities.length > 0 && (
              <Box
                sx={{
                  maxWidth: '100%',
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  // Ensure the inner table/grid is allowed to be wider than the message bubble if needed
                  '& > *': { minWidth: 'max-content' }
                }}
              >
                <NRRiseAIVulnerabilitiesTable
                  vulnerabilities={nrvulnerabilities}
                  tableRef={vulnerabilitiesTableRef}
                  isLoading={false}
                  tableStorageId="RiseAIChatVulnerabilitiesBasicTable"
                  disabledProviders={[]}
                />
              </Box>
            )}
            {message.error && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  mt: 1,
                  display: 'block',
                  fontWeight: 600
                }}
              >
                Error occurred
              </Typography>
            )}
          </Paper>
        </Box>
      )}
    </React.Fragment>
  );
}
